import React, { useState, useEffect, useMemo } from "react";
import { useSelector } from "react-redux";
import { useParams, useNavigate } from "react-router-dom";
import { Button } from "react-bootstrap";
import Loading from "../../helpers/Loading";
import { getUserById } from "../../../data/getters";
import ErrorModal from "../../helpers/ErrorModal";
import authApi from "../../../data/api/authApi";
import { useLanguage } from "../../helpers/LanguageContext";
import formatPrice from "../../helpers/formatPrice";
import CustomTable from "../../helpers/customtable";
function UserAnalyzer() {
  const navigate = useNavigate();
  const { userId } = useParams();
  const { t, language } = useLanguage();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [selectedUser, setSelectedUser] = useState(null);

  const currentUser = useSelector((s) => s.auth.user);
  // A Redux-store-ból vesszük az összes munkát, majd kiszűrjük a user-hez tartozókat
  const allWorks = useSelector((state) => state.works || []);
  console.log(allWorks);
  const isAdmin = currentUser?.role === "admin";
  const userWorks = useMemo(() => {
    return allWorks.filter((w) => {
      return (
        String(w.user?.userId) === String(userId) &&
        (isAdmin || w.isOrdered === true)
      );
    });
  }, [allWorks, userId, isAdmin]);
  // --- Felhasználó és inicializáló logika ---
  useEffect(() => {
    async function loadData() {
      setLoading(true);
      try {
        // Lekérjük a felhasználó adatait
        const userData = await getUserById(userId);
        if (!userData) {
          throw new Error("Felhasználó nem található");
        }
        setSelectedUser(userData);
      } catch (err) {
        console.error(err);
        setError(err.message || "Failed to load user data");
      } finally {
        setLoading(false);
      }
    }

    loadData();
  }, [userId]);

  const workList = useMemo(
    () =>
      userWorks.map((work) => ({
        ...work,
        paid: work.paid ?? 0,
        price: work.price ?? 0
      })),
    [userWorks]
  );

  const handleDeleteUser = async () => {
    if (!window.confirm(t("deleteUserConfirm"))) return;
    try {
      setLoading(true);
      await authApi.deleteUserApi(userId);
      // siker esetén vissza a dashboardra (vagy máshová)
      navigate("/dashboard");
    } catch (err) {
      console.error(err);
      setError(err.message || t("deleteUserFailed"));
    } finally {
      setLoading(false);
    }
  };

  // --- Sorra kattintva: WorkAnalyzer-ra navigálunk ---
  const handleRowClick = (workId) => {
    navigate(`/work/${workId}`);
  };

  const handleCloseErrorModal = () => setError("");

  const dateLocale =
    language === "en" ? "en-US" : language === "ro" ? "ro-RO" : "hu-HU";
  const tableConfig = {
    rowKey: "workId",
    emptyText: t("noOrdersFromUser"),
    pageSize: 10,
    showPageSizeSelector: true,
    pageSizeOptions: [10, 25, 50, 100],
    showTotalCount: true,
    onRowClick: (work) => handleRowClick(work.workId),
    columns: [
      {
        key: "workId",
        header: t("orderId"),
        default: "N/A"
      },
      {
        key: "paid",
        header: t("paidAmount"),
        formatValue: (value) => formatPrice(value)
      },
      {
        key: "price",
        header: t("totalAmount"),
        formatValue: (value) => formatPrice(value)
      },
      { key: "label", header: t("labor"), default: "N/A" },
      { key: "status", header: t("status"), default: "N/A" },
      {
        key: "orderDate",
        header: t("orderDate"),
        default: "N/A",
        formatValue: (value) => new Date(value).toLocaleDateString(dateLocale)
      },
      {
        key: "finishDate",
        header: t("finishDate"),
        default: "N/A",
        formatValue: (value) => new Date(value).toLocaleDateString(dateLocale)
      }
    ]
  };

  return (
    <div style={{ height: "100vh", overflow: "hidden" }}>
      <div
        className="container d-xl-block"
        style={{
          height: "100%",
          display: "flex",
          flexDirection: "column"
        }}
      >
        {loading ? (
          <Loading />
        ) : (
          <>
            {error && (
              <ErrorModal error={error} onClose={handleCloseErrorModal} />
            )}

            {selectedUser ? (
              <>
                <div className="d-flex justify-content-between align-items-center mb-3">
                  <p className="fs-1  text-start mb-0">
                    <span className="fw-bold ff-italic">
                      {selectedUser.name}
                    </span>
                    <span> {t("userWorksTitle")}</span>
                  </p>
                </div>
                {(currentUser.role === "admin" ||
                  currentUser.role === "companyAdmin") && (
                  <Button variant="outline-danger" onClick={handleDeleteUser}>
                    {t("deleteUser")}
                  </Button>
                )}
                <div className="d-flex justify-content-between align-items-center mb-3">
                  <p className="fs-2 fw-bold text-start mb-0">
                    {t("allWorksTitle")}
                  </p>
                </div>

                <div
                  style={{
                    flex: 1,
                    minHeight: 0,
                    display: "flex"
                  }}
                >
                  <CustomTable data={workList} config={tableConfig} />
                </div>
              </>
            ) : (
              <div className="d-flex justify-content-center mt-3">
                <ErrorModal
                  error="User not found"
                  onClose={handleCloseErrorModal}
                />
                <Button
                  variant="primary"
                  onClick={() => navigate("/dashboard")}
                >
                  Vissza a kezdőlapra
                </Button>
              </div>
            )}
          </>
        )}
      </div>
    </div>
  );
}

export default UserAnalyzer;
